import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 * Patagonia-style hide on scroll down, show on scroll up.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Last scroll position for direction detection
   * @type {number}
   */
  #lastScrollY = 0;

  /**
   * Prevents excessive scroll calculations
   * @type {boolean}
   */
  #ticking = false;

  /**
   * Minimum scroll delta before triggering hide/show
   * @type {number}
   */
  #delta = 10;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Simple, clean scroll handler - Patagonia style
   * Hide on scroll down, show on scroll up
   */
  #onScroll = () => {
    if (!this.#ticking) {
      requestAnimationFrame(() => this.#updateScrollState());
      this.#ticking = true;
    }
  };

  /**
   * Update header visibility based on scroll direction
   */
  #updateScrollState() {
    const scrollY = window.scrollY;
    const headerHeight = this.offsetHeight;

    // Handle transparent → opaque transition (at 50px scroll)
    if (scrollY > 50) {
      this.classList.add('header-scrolled');
    } else {
      this.classList.remove('header-scrolled');
    }

    // Handle hide/reveal (only act if scroll delta exceeds threshold)
    if (Math.abs(scrollY - this.#lastScrollY) > this.#delta) {
      if (scrollY > this.#lastScrollY && scrollY > headerHeight) {
        // Scrolling DOWN past header height - hide
        this.classList.add('header-hidden');
      } else {
        // Scrolling UP - show
        this.classList.remove('header-hidden');
      }
      this.#lastScrollY = scrollY;
    }

    this.#ticking = false;
  }

  connectedCallback() {
    // Attach scroll listener FIRST, before any ref validation
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      this.#lastScrollY = window.scrollY;
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    // Now do the rest (may fail if refs missing, but scroll still works)
    try {
      super.connectedCallback();
      this.#resizeObserver.observe(this);
      this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

      if (this.refs?.headerRowTop) {
        changeMetaThemeColor(this.refs.headerRowTop);
      }
    } catch {
      // Header refs handled gracefully
    }
  }

  updatedCallback() {
    try {
      super.updatedCallback();
    } catch (e) {
      // Ignore parent errors
    }

    // Re-initialize scroll state after page navigation (morphing)
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      // Reset scroll position tracking for new page
      this.#lastScrollY = window.scrollY;

      // Remove old listener and add fresh one (prevents duplicates)
      window.removeEventListener('scroll', this.#onScroll);
      window.addEventListener('scroll', this.#onScroll, { passive: true });

      // Reset classes for fresh page state
      this.classList.remove('header-scrolled', 'header-hidden');
      if (window.scrollY > 50) {
        this.classList.add('header-scrolled');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('scroll', this.#onScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
